// readinessScore.js — deterministic 0–100 training-readiness score.
//
// Mirrors the AI Calorie Target engine pattern: the *number* is computed here
// in code (reproducible, instant, trackable day-to-day), and the LLM only
// narrates / handles nuance on top. The HARD/MEDIUM/EASY/REST label is just a
// band of this score; RECOVERY overrides it when the user already trained.
//
// Each factor maps its signal to a 0–100 sub-score; the readiness score is the
// weight-normalised average over whichever factors have data. Contributions are
// each factor's deviation from a neutral BASELINE, so they sum to (score − 70)
// — that's what drives the "why" bars in the UI.

const BASELINE = 70; // neutral anchor for contribution math

const clamp = (v, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, v));
const n = (v) => (v == null || !Number.isFinite(v)) ? null : v;

// ── Per-factor sub-scores (0–100, higher = more recovered) ──────────────
function hrvSub(hrv) {
  let d = n(hrv?.deltaPct);
  if (d == null) d = n(hrv?.trend3vs14Pct);
  if (d == null) return null;
  return clamp(85 + d * 2.2); // +10% → 100, 0% → 85, −15% → 52, −25% → 30
}
function sleepSub(sleep) {
  let h = n(sleep?.totalHours);
  if (h == null) h = n(sleep?.avg7dHours);
  if (h == null) return null;
  let s = clamp((h - 3.5) * 22); // 5h→33, 6h→55, 7h→77, 8h→99
  const debt = n(sleep?.debt7dHours) || 0;
  s -= Math.min(30, debt * 2); // 7h debt → −14, 15h → −30
  const q = n(sleep?.deepPlusRemMin) || 0;
  if (q >= 90) s += 5; else if (q > 0 && q < 60) s -= 5;
  return clamp(s);
}
function rhrSub(rhr) {
  let d = n(rhr?.deltaBpm);
  if (d == null) d = n(rhr?.trend3vs14Bpm);
  if (d == null) return null;
  return clamp(85 - d * 7); // −3bpm → 100, +5 → 50, +10 → 15
}
function loadSub(training) {
  const parts = [];
  const acwr = n(training?.acwr);
  if (acwr != null) {
    let a;
    if (acwr < 0.8) a = 85;          // detraining — fine to train
    else if (acwr <= 1.3) a = 100;   // sweet spot
    else a = clamp(100 - (acwr - 1.3) * 100); // 1.5→80, 1.8→50, 2.0→30
    parts.push(a);
  }
  const rec = n(training?.hoursSinceLastSession);
  if (rec != null) {
    parts.push(rec >= 48 ? 100 : rec >= 36 ? 85 : rec >= 24 ? 65 : 45);
  }
  if (!parts.length) return null;
  let s = parts.reduce((a, b) => a + b, 0) / parts.length;
  const consec = n(training?.consecutiveTrainingDays) || 0;
  if (consec >= 5) s -= 20; else if (consec === 4) s -= 8;
  return clamp(s);
}
function trendSub(hrv, rhr) {
  const ht = n(hrv?.trend3vs14Pct), rt = n(rhr?.trend3vs14Bpm);
  if (ht == null && rt == null) return null;
  let s = 70;
  if (ht != null) s += ht * 2;   // rising HRV trend → up
  if (rt != null) s -= rt * 5;   // rising RHR trend → down
  return clamp(s);
}

const FACTORS = [
  { key: 'hrv',   label: 'HRV',           weight: 0.30, fn: (i) => hrvSub(i.hrv) },
  { key: 'sleep', label: 'Sleep',         weight: 0.25, fn: (i) => sleepSub(i.sleep) },
  { key: 'rhr',   label: 'Resting HR',    weight: 0.15, fn: (i) => rhrSub(i.rhr) },
  { key: 'load',  label: 'Training load', weight: 0.20, fn: (i) => loadSub(i.training) },
  { key: 'trend', label: '3-day trend',   weight: 0.10, fn: (i) => trendSub(i.hrv, i.rhr) },
];

function blend(active) {
  const wsum = active.reduce((s, f) => s + f.weight, 0) || 1;
  return active.reduce((s, f) => s + (f.weight / wsum) * f.sub, 0);
}

export function bandForScore(score, trainedToday) {
  if (trainedToday) return 'RECOVERY';
  return score >= 80 ? 'HARD' : score >= 60 ? 'MEDIUM' : score >= 40 ? 'EASY' : 'REST';
}

// ── Subjective feel — the user's own read on their body ──────────────────
// Objective sensors miss sleep quality, stress, early illness, CNS/mental
// fatigue. A self-reported feel can only pull the recommendation DOWN (you
// know things the watch doesn't); it never pushes you to train harder than the
// data supports. Stored per-day so it resets each morning.
export const FEEL_OPTIONS = [
  { key: 'strong',  emoji: '💪',  label: 'Strong',  cap: null },
  { key: 'good',    emoji: '🙂',  label: 'Good',    cap: null },
  { key: 'off',     emoji: '😮‍💨', label: 'Off',     cap: 'MEDIUM' },
  { key: 'drained', emoji: '🥵',  label: 'Drained', cap: 'REST' },
];
const FEEL_KEY = 'hayahReadinessFeel';
function feelDateStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export function loadFeel() {
  try { const r = JSON.parse(localStorage.getItem(FEEL_KEY) || 'null'); return (r && r.date === feelDateStr()) ? r.feel : null; }
  catch { return null; }
}
export function saveFeel(feel) {
  try { localStorage.setItem(FEEL_KEY, JSON.stringify({ date: feelDateStr(), feel })); } catch {}
}
// Apply the feel as a downward-only cap on a band. Returns { band, capped, note }.
export function capByFeel(band, feelKey) {
  if (!feelKey || band === 'RECOVERY') return { band, capped: false, note: null };
  const opt = FEEL_OPTIONS.find(o => o.key === feelKey);
  if (!opt || !opt.cap) return { band, capped: false, note: null };
  if (BAND_ORDER.indexOf(opt.cap) < BAND_ORDER.indexOf(band)) {
    return { band: opt.cap, capped: true, note: `You reported feeling ${opt.label.toLowerCase()} — held at ${opt.cap}. Your read on your body overrides the sensors.` };
  }
  return { band, capped: false, note: null };
}

// Gates: hard disqualifiers that CAP the recommendation regardless of how good
// the recovery score is. A blended score alone is too lenient — two strong
// markers (HRV + RHR) shouldn't green-light HARD the day after a session, or
// through an ACWR spike. These mirror the original EASY/REST trigger rules.
// Returns { band, gates: [{ factorKey, reason }] } where band is the score band
// lowered to the most conservative cap any gate imposes.
const BAND_ORDER = ['REST', 'EASY', 'MEDIUM', 'HARD'];
function applyGates(scoreBand, inputs) {
  const gates = [];
  let capIdx = BAND_ORDER.indexOf(scoreBand);
  const cap = (maxBand, factorKey, reason) => {
    gates.push({ factorKey, reason, cap: maxBand });
    capIdx = Math.min(capIdx, BAND_ORDER.indexOf(maxBand));
  };
  const t = inputs.training || {}, hrv = inputs.hrv || {}, rhr = inputs.rhr || {}, sleep = inputs.sleep || {};
  // NOTE: no global "hours since last session" gate. On a split routine you can
  // train hard daily by rotating muscle groups — the 48–72h rule is PER MUSCLE
  // and is handled by the muscle-readiness chips, not the systemic band. The
  // global band is capped only by whole-body fatigue signals below.
  if (n(t.acwr) != null && t.acwr > 1.5) cap('EASY', 'load', `ACWR ${t.acwr} — acute load spike (>1.5)`);
  if ((n(t.consecutiveTrainingDays) || 0) >= 6) cap('EASY', 'load', `${t.consecutiveTrainingDays} straight training days — systemic fatigue`);
  if (n(hrv.deltaPct) != null && hrv.deltaPct <= -25) cap('REST', 'hrv', `HRV ${Math.abs(hrv.deltaPct)}% below baseline`);
  else if (n(hrv.deltaPct) != null && hrv.deltaPct <= -15) cap('EASY', 'hrv', `HRV ${Math.abs(hrv.deltaPct)}% below baseline`);
  if (n(rhr.deltaBpm) != null && rhr.deltaBpm >= 7) cap('EASY', 'rhr', `RHR +${Math.round(rhr.deltaBpm)} bpm over baseline`);
  if (n(sleep.totalHours) != null && sleep.totalHours < 5) cap('REST', 'sleep', `only ${sleep.totalHours}h sleep`);
  else if (n(sleep.totalHours) != null && sleep.totalHours < 6) cap('EASY', 'sleep', `only ${sleep.totalHours}h sleep`);
  if ((n(sleep.debt7dHours) || 0) > 12) cap('MEDIUM', 'sleep', `${sleep.debt7dHours}h sleep debt this week`);
  // Surface the gates that actually demoted the band below the recovery-score
  // band — those are the "this is why you're not cleared higher" reasons.
  const scoreIdx = BAND_ORDER.indexOf(scoreBand);
  const binding = gates.filter(g => BAND_ORDER.indexOf(g.cap) < scoreIdx);
  return { band: BAND_ORDER[capIdx], gates: binding, cappedFromScore: capIdx < scoreIdx };
}

// Main entry. Returns { score, band, confidence, contributions[], whatLifts[],
// cappedNote, dehydrated } or null if there's nothing to score.
export function scoreReadiness(inputs) {
  if (!inputs) return null;
  const dehydrated = (n(inputs.body?.overnightDeltaKg) ?? 0) <= -1.5;
  const illness = (n(inputs.recovery?.wristTempDeltaC) ?? 0) > 0.30 && (n(inputs.hrv?.deltaPct) ?? 0) <= -10;

  // Dehydration makes HRV mechanically low — drop it from the blend so a fluid
  // shift doesn't read as fatigue.
  const active = [];
  for (const f of FACTORS) {
    if (dehydrated && f.key === 'hrv') continue;
    const sub = f.fn(inputs);
    if (sub == null) continue;
    active.push({ ...f, sub });
  }
  if (!active.length) return null;

  const wsum = active.reduce((s, f) => s + f.weight, 0);
  let score = Math.round(blend(active));
  const contributions = active
    .map(f => ({ key: f.key, label: f.label, sub: Math.round(f.sub), points: +((f.weight / wsum) * (f.sub - BASELINE)).toFixed(1) }))
    .sort((a, b) => Math.abs(b.points) - Math.abs(a.points));

  let cappedNote = null;
  if (illness) { score = Math.min(score, 22); cappedNote = 'Capped — wrist temp + HRV drop read as illness, not fatigue.'; }

  // Band = recovery-score band, then lowered by any hard disqualifier (gate).
  const trainedToday = !!inputs.training?.trainedToday;
  const scoreBand = bandForScore(score, trainedToday);
  let band = scoreBand, gates = [], cappedFromScore = false;
  if (!trainedToday) {
    const g = applyGates(scoreBand, inputs);
    band = g.band; gates = g.gates; cappedFromScore = g.cappedFromScore;
  }
  const gatedFactors = new Set(gates.map(g => g.factorKey));
  const confidence = active.length >= 4 ? 'HIGH' : active.length >= 3 ? 'MODERATE' : 'LOW';

  // ── "What lifts it" — single-lever hypotheticals (forward-looking) ──
  const whatLifts = [];
  const recompute = (key, newSub) => Math.round(blend(active.map(f => f.key === key ? { ...f, sub: newSub } : f)));
  const sleepNow = active.find(f => f.key === 'sleep');
  if (sleepNow && sleepNow.sub < 88) {
    const eightHr = sleepSub({ ...inputs.sleep, totalHours: 8, debt7dHours: Math.max(0, (inputs.sleep?.debt7dHours || 0) - 1) });
    const r = recompute('sleep', eightHr);
    if (r > score) whatLifts.push({ label: 'Sleep 8h tonight', resultScore: r, delta: r - score, tomorrow: true });
  }
  const loadNow = active.find(f => f.key === 'load');
  if (loadNow && (inputs.training?.hoursSinceLastSession ?? 99) < 48) {
    const rested = loadSub({ ...inputs.training, hoursSinceLastSession: 48, consecutiveTrainingDays: 0 });
    const r = recompute('load', rested);
    if (r > score) whatLifts.push({ label: 'Rest today', resultScore: r, delta: r - score, tomorrow: true });
  }
  if (dehydrated) {
    // Re-add HRV at its trend value to show what rehydrating reveals.
    const hv = hrvSub(inputs.hrv);
    if (hv != null) {
      const reAdded = [...active, { key: 'hrv', label: 'HRV', weight: 0.30, sub: hv }];
      const r = Math.round(blend(reAdded));
      whatLifts.push({ label: 'Rehydrate', resultScore: r, delta: r - score, tomorrow: false });
    }
  }

  // Gap to the next band up (actionable target).
  const nextThreshold = score < 40 ? 40 : score < 60 ? 60 : score < 80 ? 80 : null;
  const toNextBand = nextThreshold ? { points: nextThreshold - score, band: bandForScore(nextThreshold, false) } : null;

  // Flag the factors that gated the band so the UI can render them red.
  contributions.forEach(c => { c.concern = gatedFactors.has(c.key); });

  return {
    score, band, scoreBand, confidence, contributions,
    whatLifts: whatLifts.slice(0, 3), toNextBand,
    gates, cappedFromScore, cappedNote, dehydrated, factorsUsed: active.length,
  };
}
